/** 角色卡 / 世界书（Preset）仓储：CRUD + 名称/来源检索 + 分页。 */

import type { Table } from 'dexie';

import { getDb } from '../db';
import { newId } from '../ids';
import type { Preset } from '../types';
import {
  compareNameThenId,
  decodeNameCursor,
  encodeNameCursor,
  normalizeLimit,
  type Page,
  type PageQuery,
} from './pagination';

export interface CreatePresetInput {
  id?: string;
  name: string;
  avatar?: string;
  description?: string;
  systemPrompt: string;
  modelConfig?: Preset['modelConfig'];
  worldBook?: Preset['worldBook'];
  tags?: string[];
  source?: Preset['source'];
  automationMode?: Preset['automationMode'];
  autoImageEnabled?: boolean;
  imageGenerationPolicy?: Preset['imageGenerationPolicy'];
  imageProviderId?: string;
  imageModel?: string;
  autoTtsEnabled?: boolean;
  ttsGenerationPolicy?: Preset['ttsGenerationPolicy'];
  ttsProviderId?: string;
  ttsModel?: string;
  ttsVoiceMode?: Preset['ttsVoiceMode'];
  ttsAutoVoiceDesign?: boolean;
  quietPeriods?: Preset['quietPeriods'];
}

export interface PresetQuery extends PageQuery {
  /** 名称模糊匹配（不区分大小写）。 */
  keyword?: string;
  source?: Preset['source'];
  tag?: string;
}

function table(): Table<Preset, string> {
  return getDb().presets;
}

export function createPreset(input: CreatePresetInput): Preset {
  return {
    id: input.id ?? newId(),
    name: input.name,
    systemPrompt: input.systemPrompt,
    ...(input.modelConfig !== undefined ? { modelConfig: input.modelConfig } : {}),
    ...(input.avatar !== undefined ? { avatar: input.avatar } : {}),
    ...(input.description !== undefined ? { description: input.description } : {}),
    ...(input.worldBook !== undefined ? { worldBook: input.worldBook } : {}),
    ...(input.tags !== undefined ? { tags: input.tags } : {}),
    ...(input.source !== undefined ? { source: input.source } : {}),
    ...(input.automationMode !== undefined ? { automationMode: input.automationMode } : {}),
    ...(input.autoImageEnabled !== undefined ? { autoImageEnabled: input.autoImageEnabled } : {}),
    ...(input.imageGenerationPolicy !== undefined ? { imageGenerationPolicy: input.imageGenerationPolicy } : {}),
    ...(input.imageProviderId !== undefined ? { imageProviderId: input.imageProviderId } : {}),
    ...(input.imageModel !== undefined ? { imageModel: input.imageModel } : {}),
    ...(input.autoTtsEnabled !== undefined ? { autoTtsEnabled: input.autoTtsEnabled } : {}),
    ...(input.ttsGenerationPolicy !== undefined ? { ttsGenerationPolicy: input.ttsGenerationPolicy } : {}),
    ...(input.ttsProviderId !== undefined ? { ttsProviderId: input.ttsProviderId } : {}),
    ...(input.ttsModel !== undefined ? { ttsModel: input.ttsModel } : {}),
    ...(input.ttsVoiceMode !== undefined ? { ttsVoiceMode: input.ttsVoiceMode } : {}),
    ...(input.ttsAutoVoiceDesign !== undefined ? { ttsAutoVoiceDesign: input.ttsAutoVoiceDesign } : {}),
    ...(input.quietPeriods !== undefined ? { quietPeriods: input.quietPeriods } : {}),
  };
}

export async function putPreset(preset: Preset): Promise<string> {
  return table().put(preset);
}

export async function createAndSavePreset(input: CreatePresetInput): Promise<Preset> {
  const preset = createPreset(input);
  await putPreset(preset);
  return preset;
}

export async function getPreset(id: string): Promise<Preset | undefined> {
  return table().get(id);
}

export async function listPresets(query: PresetQuery = {}): Promise<Preset[]> {
  const all = await table().toArray();
  const keyword = query.keyword?.trim().toLowerCase();
  return all
    .filter((preset) => {
      if (query.source && preset.source !== query.source) return false;
      if (query.tag && !(preset.tags ?? []).includes(query.tag)) return false;
      if (keyword && !preset.name.toLowerCase().includes(keyword)) return false;
      return true;
    })
    .sort(compareNameThenId);
}

/** 按名称分页（游标与列表排序规则一致）。 */
export async function listPresetsPage(query: PresetQuery = {}): Promise<Page<Preset>> {
  const limit = normalizeLimit(query.limit);
  const all = await listPresets(query);
  const cursor = query.cursor ? decodeNameCursor(query.cursor) : null;
  const ordered = cursor ? all.filter((p) => compareNameThenId(p, cursor) > 0) : all;

  const items = ordered.slice(0, limit);
  const hasMore = ordered.length > limit;
  const last = items[items.length - 1];
  return {
    items,
    hasMore,
    nextCursor: hasMore && last ? encodeNameCursor(last.name, last.id) : null,
  };
}

export async function updatePreset(
  id: string,
  patch: Partial<Omit<Preset, 'id'>>,
): Promise<Preset | undefined> {
  const existing = await getPreset(id);
  if (!existing) return undefined;
  const next: Preset = { ...existing, ...patch, id };
  await putPreset(next);
  return next;
}

export async function deletePreset(id: string): Promise<void> {
  await table().delete(id);
}

export async function countPresets(): Promise<number> {
  return table().count();
}

export async function clearPresets(): Promise<void> {
  await table().clear();
}
